import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { 
  Search,
  Calendar,
  User,
  Clock,
  TrendingUp,
  FileText,
  Filter,
  Download,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Eye,
  MessageCircle, 
  Edit          
} from 'lucide-react';
import toast from 'react-hot-toast';
import configService from '../../services/configService';

const TicketLogs = () => {
  const [selectedTicketId, setSelectedTicketId] = useState('');
  const [systemLogsFilters, setSystemLogsFilters] = useState({
    date_from: '',
    date_to: '',
    action: '',
    user_id: '',
    priority: '',
    per_page: 50
  });
  
  // NEW: Analytics date filters - separate state
  const [analyticsFilters, setAnalyticsFilters] = useState({
    date_from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    date_to: new Date().toISOString().split('T')[0] // today
  });
  
  const [activeTab, setActiveTab] = useState('ticket');
  const [showFilters, setShowFilters] = useState(false);
  const [showAnalyticsFilters, setShowAnalyticsFilters] = useState(false); // NEW

  // Query for specific ticket logs
  const { data: ticketLogs, isLoading: ticketLoading, refetch: refetchTicket } = useQuery({
    queryKey: ['ticket-logs', selectedTicketId],
    queryFn: () => configService.getTicketLogs(selectedTicketId),
    enabled: !!selectedTicketId
  });

  // Query for system logs
  const { data: systemLogs, isLoading: systemLoading, refetch: refetchSystem } = useQuery({
    queryKey: ['system-logs', systemLogsFilters],
    queryFn: () => configService.getSystemLogs(systemLogsFilters),
    enabled: activeTab === 'system'
  });

  // UPDATED: Query for analytics with date filters
  const { data: analytics, isLoading: analyticsLoading, refetch: refetchAnalytics } = useQuery({
    queryKey: ['log-analytics', analyticsFilters],
    queryFn: () => configService.getLogAnalytics(analyticsFilters),
    enabled: activeTab === 'analytics'
  });

  const handleTicketSearch = (e) => {
    e.preventDefault();
    if (!selectedTicketId.trim()) {
      toast.error('Please enter a ticket ID');
      return;
    }
    refetchTicket();
  };

  const handleFilterChange = (key, value) => {
    setSystemLogsFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // NEW: Handle analytics filter changes
  const handleAnalyticsFilterChange = (key, value) => {
    setAnalyticsFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setSystemLogsFilters({
      date_from: '',
      date_to: '',
      action: '',
      user_id: '',
      priority: '',
      per_page: 50
    });
  };

  // NEW: Clear analytics filters
  const clearAnalyticsFilters = () => {
    setAnalyticsFilters({
      date_from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      date_to: new Date().toISOString().split('T')[0]
    });
  };

  // NEW: Apply analytics filters manually (optional - auto-applies with useQuery)
  const applyAnalyticsFilters = () => {
    if (!analyticsFilters.date_from || !analyticsFilters.date_to) {
      toast.error('Please select both start and end dates');
      return;
    }
    
    if (new Date(analyticsFilters.date_from) > new Date(analyticsFilters.date_to)) {
      toast.error('Start date cannot be later than end date');
      return;
    }
    
    refetchAnalytics();
    toast.success('Analytics updated with new date range');
  };

  // NEW: Quick date presets
  const setQuickDateRange = (days) => {
    const endDate = new Date();
    const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
    
    setAnalyticsFilters({
      date_from: startDate.toISOString().split('T')[0],
      date_to: endDate.toISOString().split('T')[0]
    });
  };

  const getActionTypeColor = (type) => {
    const colors = {
      'creation': 'bg-blue-100 text-blue-800',
      'assignment': 'bg-green-100 text-green-800',
      'escalation': 'bg-red-100 text-red-800',
      'transfer': 'bg-yellow-100 text-yellow-800',
      'return_transfer': 'bg-blue-100 text-blue-800',
      'communication': 'bg-purple-100 text-purple-800',
      'modification': 'bg-indigo-100 text-indigo-800',
      'status': 'bg-orange-100 text-orange-800',
      'resolution': 'bg-emerald-100 text-emerald-800',
      'general': 'bg-gray-100 text-gray-800'
    };
    return colors[type] || colors.general;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'bg-green-100 text-green-800',
      'medium': 'bg-blue-100 text-blue-800',
      'high': 'bg-orange-100 text-orange-800',
      'critical': 'bg-red-100 text-red-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const tabs = [
    { id: 'ticket', label: 'Ticket Lifecycle', icon: FileText },
    { id: 'system', label: 'System Logs', icon: User },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <FileText className="w-6 h-6 mr-2" />
          Ticket Logs & Analytics
        </h1>
        <p className="mt-2 text-gray-600">
          View complete ticket lifecycle, system logs, and performance analytics
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Ticket Lifecycle Tab */}
          {activeTab === 'ticket' && (
            <div className="space-y-6">
              {/* Search Form */}
              <form onSubmit={handleTicketSearch} className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ticket ID
                  </label>
                  <input
                    type="text"
                    value={selectedTicketId}
                    onChange={(e) => setSelectedTicketId(e.target.value)}
                    placeholder="Enter ticket ID to view complete lifecycle"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="submit"
                    disabled={ticketLoading}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
                  >
                    {ticketLoading ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4 mr-2" />
                    )}
                    Search
                  </button>
                </div>
              </form>

              {/* Ticket Information */}
              {ticketLogs?.data && (
                <div className="space-y-6">
                  {/* Ticket Overview */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Ticket ID</p>
                        <p className="text-lg font-semibold">
                          {ticketLogs.data.ticket.custom_id || `#${ticketLogs.data.ticket.id}`}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Status</p>
                        <p className="text-lg font-semibold capitalize">{ticketLogs.data.ticket.status}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Priority</p>
                        <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(ticketLogs.data.ticket.priority)}`}>
                          {ticketLogs.data.ticket.priority?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Age</p>
                        <p className="text-lg font-semibold">
                          {ticketLogs.data.statistics?.ticket_age?.formatted || 'Unknown'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Statistics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white border rounded-lg p-4">
                      <div className="flex items-center">
                        <Clock className="w-5 h-5 text-blue-500 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Response Time</p>
                          <p className="text-sm text-gray-500">
                            {ticketLogs.data.statistics?.response_time?.formatted || 'No response yet'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border rounded-lg p-4">
                      <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Resolution Time</p>
                          <p className="text-sm text-gray-500">
                            {ticketLogs.data.statistics?.resolution_time?.formatted || 'Not resolved'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border rounded-lg p-4">
                      <div className="flex items-center">
                        <TrendingUp className="w-5 h-5 text-orange-500 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Escalations</p>
                          <p className="text-sm text-gray-500">
                            {ticketLogs.data.statistics?.total_escalations || 0} times
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border rounded-lg p-4">
                      <div className="flex items-center">
                        <ArrowRight className="w-5 h-5 text-purple-500 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Forwards</p>
                          <p className="text-sm text-gray-500">
                            {ticketLogs.data.statistics?.total_forwards || 0} times
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div className="bg-white border rounded-lg">
                    <div className="px-4 py-3 border-b border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900">Ticket Timeline</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Complete lifecycle showing who did what and when
                      </p>
                    </div>
                    <div className="p-4">
                      <div className="flow-root">
                        <ul className="-mb-8">
                          {ticketLogs.data.timeline?.map((event, index) => (
                            <li key={index}>
                              <div className="relative pb-8">
                                {index !== ticketLogs.data.timeline.length - 1 && (
                                  <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" />
                                )}
                                <div className="relative flex space-x-3">
                                  <div className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${getActionTypeColor(event.type)}`}>
                                    {event.type === 'creation' && <FileText className="w-4 h-4" />}
                                    {event.type === 'assignment' && <User className="w-4 h-4" />}
                                    {event.type === 'escalation' && <TrendingUp className="w-4 h-4" />}
                                    {event.type === 'transfer' && !event.is_return_forward && <ArrowRight className="w-4 h-4" />}
                                    {(event.type === 'return_transfer' || (event.type === 'transfer' && event.is_return_forward)) && <ArrowLeft className="w-4 h-4" />}
                                    {event.type === 'communication' && <MessageCircle className="w-4 h-4" />}
                                    {event.type === 'status' && <CheckCircle className="w-4 h-4" />}
                                    {event.type === 'resolution' && <CheckCircle className="w-4 h-4" />}
                                    {event.type === 'modification' && <Edit className="w-4 h-4" />}
                                    {!['creation', 'assignment', 'escalation', 'transfer', 'communication', 'status', 'resolution', 'modification'].includes(event.type) && <Clock className="w-4 h-4" />}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-sm">
                                      <p className="font-medium text-gray-900">
                                        {event.description}
                                      </p>
                                      <div className="flex flex-wrap items-center gap-2 mt-1 text-gray-500">
                                        <span>by {event.actor}</span>
                                        <span>•</span>
                                        <span>{new Date(event.timestamp).toLocaleString()}</span>
                                        {event.time_since_creation && (
                                          <>
                                            <span>•</span>
                                            <span className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                                              {event.time_since_creation} after creation
                                            </span>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Enhanced Details Section */}
                                    {event.details && Object.keys(event.details).length > 0 && (
                                      <div className="mt-3 text-sm">
                                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                          <div className="space-y-2">
                                            
                                            {/* Assignment Details */}
                                            {event.details.assigned_to_name && (
                                              <div className="text-sm">
                                                <span className="font-medium text-gray-700">Assigned to: </span>
                                                <span className="text-gray-900">{event.details.assigned_to_name}</span>
                                                {event.details.assigned_to_level && (
                                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-2">
                                                    Level {event.details.assigned_to_level}
                                                  </span>
                                                )}
                                              </div>
                                            )}

                                            {/* Escalation Details */}
                                            {event.details.escalated_to_name && (
                                              <div className="space-y-1">
                                                <div className="text-sm">
                                                  <span className="font-medium text-gray-700">Escalated to: </span>
                                                  <span className="text-gray-900">{event.details.escalated_to_name}</span>
                                                  {event.details.escalated_to_level && (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 ml-2">
                                                      Level {event.details.escalated_to_level}
                                                    </span>
                                                  )}
                                                </div>
                                                {event.details.escalated_from_name && (
                                                  <div className="text-sm">
                                                    <span className="font-medium text-gray-700">Escalated from: </span>
                                                    <span className="text-gray-900">{event.details.escalated_from_name}</span>
                                                    {event.details.escalated_from_level && (
                                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 ml-2">
                                                        Level {event.details.escalated_from_level}
                                                      </span>
                                                    )}
                                                  </div>
                                                )}
                                                {event.details.escalation_reason && (
                                                  <div className="text-sm">
                                                    <span className="font-medium text-gray-700">Reason: </span>
                                                    <span className="text-gray-600 italic">"{event.details.escalation_reason}"</span>
                                                  </div>
                                                )}
                                              </div>
                                            )}

                                            {/* Forward Details */}
                                            {event.details.forwarded_to_name && (
                                              <div className="space-y-1">
                                                <div className="text-sm">
                                                  <span className="font-medium text-gray-700">Forwarded to: </span>
                                                  <span className="text-gray-900">{event.details.forwarded_to_name}</span>
                                                  {event.details.forwarded_to_level && (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 ml-2">
                                                      Level {event.details.forwarded_to_level}
                                                    </span>
                                                  )}
                                                </div>
                                                {event.details.forwarded_from_name && (
                                                  <div className="text-sm">
                                                    <span className="font-medium text-gray-700">Forwarded from: </span>
                                                    <span className="text-gray-900">{event.details.forwarded_from_name}</span>
                                                    {event.details.forwarded_from_level && (
                                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 ml-2">
                                                        Level {event.details.forwarded_from_level}
                                                      </span>
                                                    )}
                                                  </div>
                                                )}
                                                {event.details.forward_reason && (
                                                  <div className="text-sm">
                                                    <span className="font-medium text-gray-700">Reason: </span>
                                                    <span className="text-gray-600 italic">"{event.details.forward_reason}"</span>
                                                  </div>
                                                )}
                                              </div>
                                            )}

                                            {/* Status Change Details */}
                                            {event.details.old_status && event.details.new_status && (
                                              <div className="text-sm">
                                                <span className="font-medium text-gray-700">Status changed: </span>
                                                <span className="text-gray-600 capitalize">{event.details.old_status}</span>
                                                <ArrowRight className="w-3 h-3 text-gray-400 inline mx-1" />
                                                <span className="text-gray-900 capitalize font-medium">{event.details.new_status}</span>
                                              </div>
                                            )}

                                            {/* Priority Change Details */}
                                            {event.details.old_priority && event.details.new_priority && (
                                              <div className="text-sm">
                                                <span className="font-medium text-gray-700">Priority changed: </span>
                                                <span className="text-gray-600 capitalize">{event.details.old_priority}</span>
                                                <ArrowRight className="w-3 h-3 text-gray-400 inline mx-1" />
                                                <span className="text-gray-900 capitalize font-medium">{event.details.new_priority}</span>
                                              </div>
                                            )}

                                            {/* Response Time Information */}
                                            {event.details.response_time_minutes && (
                                              <div className="text-sm">
                                                <span className="font-medium text-gray-700">Response time: </span>
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                  {configService.formatDuration(event.details.response_time_minutes)}
                                                </span>
                                              </div>
                                            )}

                                            {/* SLA Status */}
                                            {event.details.was_within_sla !== undefined && (
                                              <div className="text-sm">
                                                <span className="font-medium text-gray-700">SLA Status: </span>
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                  event.details.was_within_sla 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : 'bg-red-100 text-red-800'
                                                }`}>
                                                  {event.details.was_within_sla ? 'Within SLA' : 'SLA Breached'}
                                                </span>
                                              </div>
                                            )}

                                            {/* Resolution Notes */}
                                            {event.details.resolution_notes && (
                                              <div className="text-sm">
                                                <span className="font-medium text-gray-700 block mb-1">Resolution notes:</span>
                                                <div className="bg-white border border-gray-200 rounded p-2">
                                                  <span className="text-gray-700 text-sm">{event.details.resolution_notes}</span>
                                                </div>
                                              </div>
                                            )}

                                            {/* Comment Details */}
                                            {event.details.is_private !== undefined && (
                                              <div className="text-sm">
                                                <span className="font-medium text-gray-700">Comment type: </span>
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                  event.details.is_private 
                                                    ? 'bg-red-100 text-red-800' 
                                                    : 'bg-blue-100 text-blue-800'
                                                }`}>
                                                  {event.details.is_private ? 'Private' : 'Public'}
                                                </span>
                                              </div>
                                            )}

                                            {/* Creation Type */}
                                            {event.details.creation_type && (
                                              <div className="text-sm">
                                                <span className="font-medium text-gray-700">Creation Type: </span>
                                                <span className="text-gray-900">{event.details.creation_type}</span>
                                              </div>
                                            )}

                                            {/* Actual Creator ID */}
                                            {event.details.actual_creator_id && (
                                              <div className="text-sm">
                                                <span className="font-medium text-gray-700">Actual Creator Id: </span>
                                                <span className="text-gray-900">{event.details.actual_creator_id}</span>
                                              </div>
                                            )}

                                            {/* Behalf Reason */}
                                            {event.details.behalf_reason && (
                                              <div className="text-sm">
                                                <span className="font-medium text-gray-700">Behalf Reason: </span>
                                                <span className="text-gray-900">{event.details.behalf_reason}</span>
                                              </div>
                                            )}

                                            {/* SPECIAL HANDLING for initial_data */}
                                            {event.details.initial_data && typeof event.details.initial_data === 'object' && (
                                              <div className="text-sm">
                                                <span className="font-medium text-gray-700 block mb-1">Initial Data:</span>
                                                <div className="bg-white border border-gray-200 rounded p-2 space-y-1">
                                                  {Object.entries(event.details.initial_data).map(([dataKey, dataValue]) => (
                                                    <div key={dataKey} className="text-sm">
                                                      <span className="font-medium text-gray-600">
                                                        {dataKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: 
                                                      </span>
                                                      <span className="text-gray-900 ml-1">{String(dataValue)}</span>
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            )}

                                            {/* SPECIAL HANDLING for expertise_match */}
                                            {event.details.expertise_match && typeof event.details.expertise_match === 'object' && (
                                              <div className="text-sm">
                                                <span className="font-medium text-gray-700 block mb-1">Expertise Match:</span>
                                                <div className="bg-white border border-gray-200 rounded p-2 space-y-1">
                                                  <div className="text-sm">
                                                    <span className="font-medium text-gray-600">Category: </span>
                                                    <span className="text-gray-900">{event.details.expertise_match.category_name}</span>
                                                  </div>
                                                  <div className="text-sm">
                                                    <span className="font-medium text-gray-600">Match Type: </span>
                                                    <span className="text-gray-900 capitalize">{event.details.expertise_match.match_type}</span>
                                                  </div>
                                                  <div className="text-sm">
                                                    <span className="font-medium text-gray-600">Staff Level: </span>
                                                    <span className="text-gray-900">Level {event.details.expertise_match.staff_level}</span>
                                                  </div>
                                                  <div className="text-sm">
                                                    <span className="font-medium text-gray-600">Workload: </span>
                                                    <span className="text-gray-900">{event.details.expertise_match.current_workload}/{event.details.expertise_match.capacity}</span>
                                                  </div>
                                                </div>
                                              </div>
                                            )}

                                            {/* Time Metrics */}
                                            {event.details.time_with_previous_agent && (
                                              <div className="text-sm">
                                                <span className="font-medium text-gray-700">Time with previous agent: </span>
                                                <span className="text-gray-600">
                                                  {configService.formatDuration(event.details.time_with_previous_agent)}
                                                </span>
                                              </div>
                                            )}

                                            {/* Show other details that don't have special formatting */}
                                            {Object.entries(event.details).map(([key, value]) => {
                                              // Skip keys we've already handled above
                                              const handledKeys = [
                                                'assigned_to_name', 'assigned_to_level', 'assigned_to_id',
                                                'escalated_to_name', 'escalated_to_level', 'escalated_from_name', 'escalated_from_level', 'escalation_reason',
                                                'forwarded_to_name', 'forwarded_to_level', 'forwarded_from_name', 'forwarded_from_level', 'forward_reason',
                                                'old_status', 'new_status', 'old_priority', 'new_priority',
                                                'response_time_minutes', 'was_within_sla', 'resolution_notes', 'is_private', 'time_with_previous_agent',
                                                'timestamp', 'timezone', 'creation_type', 'actual_creator_id', 'behalf_reason',
                                                'initial_data', 'expertise_match' // Skip these special objects
                                              ];
                                              
                                              if (handledKeys.includes(key) || !value) {
                                                return null;
                                              }

                                              return (
                                                <div key={key} className="text-sm">
                                                  <span className="font-medium text-gray-700">
                                                    {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: 
                                                  </span>
                                                  <span className="text-gray-600 ml-1">
                                                    {typeof value === 'object' ? (
                                                      <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">
                                                        {JSON.stringify(value, null, 2)}
                                                      </code>
                                                    ) : (
                                                      String(value)
                                                    )}
                                                  </span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* System Logs Tab */}
          {activeTab === 'system' && (
            <div className="space-y-6">
              {/* Filters */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-sm font-medium text-gray-900">Filters</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className="text-blue-600 hover:text-blue-700 text-sm flex items-center"
                    >
                      <Filter className="w-4 h-4 mr-1" />
                      {showFilters ? 'Hide' : 'Show'} Filters
                    </button>
                    <button
                      onClick={clearFilters}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                {showFilters && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date From
                      </label>
                      <input
                        type="date"
                        value={systemLogsFilters.date_from}
                        onChange={(e) => handleFilterChange('date_from', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date To
                      </label>
                      <input
                        type="date"
                        value={systemLogsFilters.date_to}
                        onChange={(e) => handleFilterChange('date_to', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Action
                      </label>
                      <select
                        value={systemLogsFilters.action}
                        onChange={(e) => handleFilterChange('action', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">All Actions</option>
                        <option value="created">Created</option>
                        <option value="updated">Updated</option>
                        <option value="assigned">Assigned</option>
                        <option value="escalated">Escalated</option>
                        <option value="forwarded">Forwarded</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Priority
                      </label>
                      <select
                        value={systemLogsFilters.priority}
                        onChange={(e) => handleFilterChange('priority', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">All Priorities</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Per Page
                      </label>
                      <select
                        value={systemLogsFilters.per_page}
                        onChange={(e) => handleFilterChange('per_page', parseInt(e.target.value))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* System Logs Table */}
              {systemLoading ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                  <p className="mt-2 text-gray-500">Loading system logs...</p>
                </div>
              ) : (
                <div className="bg-white border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Timestamp
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Action
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Ticket
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            User
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Priority
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {systemLogs?.data?.logs?.map((log) => (
                          <tr key={log.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {new Date(log.created_at).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm font-medium">
                                {configService.formatLogAction(log.action)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm">
                                <div className="font-medium text-gray-900">
                                  {log.ticket?.custom_id || `#${log.ticket?.id}`}
                                </div>
                                <div className="text-gray-500 truncate max-w-xs">
                                  {log.ticket?.title}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {log.user?.name || 'System'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {log.ticket?.priority && (
                                <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(log.ticket.priority)}`}>
                                  {log.ticket.priority.toUpperCase()}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <button
                                onClick={() => {
                                  setSelectedTicketId(log.ticket_id);
                                  setActiveTab('ticket');
                                }}
                                className="text-blue-600 hover:text-blue-700 flex items-center"
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Info */}
                  {systemLogs?.data?.pagination && (
                    <div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
                      <div className="flex justify-between items-center text-sm text-gray-500">
                        <span>
                          Page {systemLogs.data.pagination.current_page} of {systemLogs.data.pagination.total_pages}
                        </span>
                        <span>
                          Total: {systemLogs.data.pagination.total} logs
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* UPDATED: Analytics Tab with Date Filtering */}
          {activeTab === 'analytics' && (
            <div className="space-y-6">
              {/* NEW: Analytics Date Filters */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-sm font-medium text-gray-900 flex items-center">
                    <Calendar className="w-4 h-4 mr-2" />
                    Date Range Filter
                  </h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowAnalyticsFilters(!showAnalyticsFilters)}
                      className="text-blue-600 hover:text-blue-700 text-sm flex items-center"
                    >
                      <Filter className="w-4 h-4 mr-1" />
                      {showAnalyticsFilters ? 'Hide' : 'Show'} Filters
                    </button>
                    <button
                      onClick={clearAnalyticsFilters}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Reset
                    </button>
                  </div>
                </div>

                {showAnalyticsFilters && (
                  <div className="space-y-4">
                    {/* Quick Date Presets */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quick Presets
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { days: 7, label: 'Last 7 days' },
                          { days: 30, label: 'Last 30 days' },
                          { days: 90, label: 'Last 3 months' },
                          { days: 180, label: 'Last 6 months' },
                          { days: 365, label: 'Last year' }
                        ].map(preset => (
                          <button
                            key={preset.days}
                            onClick={() => setQuickDateRange(preset.days)}
                            className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Custom Date Range */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={analyticsFilters.date_from}
                          onChange={(e) => handleAnalyticsFilterChange('date_from', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          End Date
                        </label>
                        <input
                          type="date"
                          value={analyticsFilters.date_to}
                          onChange={(e) => handleAnalyticsFilterChange('date_to', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    {/* Apply Button (Optional since useQuery auto-updates) */}
                    <div className="flex justify-end">
                      <button
                        onClick={applyAnalyticsFilters}
                        disabled={analyticsLoading}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
                      >
                        {analyticsLoading ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <TrendingUp className="w-4 h-4 mr-2" />
                        )}
                        Update Analytics
                      </button>
                    </div>
                  </div>
                )}

                {/* Current Date Range Display */}
                <div className="mt-3 p-3 bg-blue-50 rounded-md">
                  <p className="text-sm text-blue-800">
                    <strong>Current Range:</strong> {' '}
                    {new Date(analyticsFilters.date_from).toLocaleDateString()} to {' '}
                    {new Date(analyticsFilters.date_to).toLocaleDateString()} {' '}
                    <span className="text-blue-600">
                      ({Math.ceil((new Date(analyticsFilters.date_to) - new Date(analyticsFilters.date_from)) / (1000 * 60 * 60 * 24))} days)
                    </span>
                  </p>
                </div>
              </div>

              {/* Analytics Content */}
              {analyticsLoading ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                  <p className="mt-2 text-gray-500">Loading analytics...</p>
                </div>
              ) : analytics?.data ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white border rounded-lg p-4">
                      <div className="flex items-center">
                        <FileText className="w-5 h-5 text-blue-500 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Total Actions</p>
                          <p className="text-lg font-semibold">{analytics.data.summary.total_actions}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white border rounded-lg p-4">
                      <div className="flex items-center">
                        <FileText className="w-5 h-5 text-green-500 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Unique Tickets</p>
                          <p className="text-lg font-semibold">{analytics.data.summary.unique_tickets}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white border rounded-lg p-4">
                      <div className="flex items-center">
                        <User className="w-5 h-5 text-purple-500 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Active Users</p>
                          <p className="text-lg font-semibold">{analytics.data.summary.active_users}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white border rounded-lg p-4">
                      <div className="flex items-center">
                        <Calendar className="w-5 h-5 text-orange-500 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Days Analyzed</p>
                          <p className="text-lg font-semibold">{Math.floor(analytics.data.summary.date_range.days)}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Breakdown */}
                  <div className="bg-white border rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Action Breakdown</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(analytics.data.actions_breakdown || {}).map(([action, count]) => (
                        <div key={action} className="text-center">
                          <div className="text-2xl font-bold text-gray-900">{count}</div>
                          <div className="text-sm text-gray-500">{configService.formatLogAction(action)}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Response Times */}
                  {analytics.data.response_times && (
                    <div className="bg-white border rounded-lg p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Response Time Analysis</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">
                            {configService.formatDuration(analytics.data.response_times.average)}
                          </div>
                          <div className="text-sm text-gray-500">Average</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">
                            {configService.formatDuration(analytics.data.response_times.median)}
                          </div>
                          <div className="text-sm text-gray-500">Median</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-orange-600">
                            {configService.formatDuration(analytics.data.response_times.min)}
                          </div>
                          <div className="text-sm text-gray-500">Fastest</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-red-600">
                            {configService.formatDuration(analytics.data.response_times.max)}
                          </div>
                          <div className="text-sm text-gray-500">Slowest</div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <AlertCircle className="w-8 h-8 mx-auto text-gray-400" />
                  <p className="mt-2 text-gray-500">No analytics data available for selected date range</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketLogs;